'use client'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { ModalsContext } from '@/context'
import type { MODALS } from '@/context'

export const useModal = (id:MODALS, openOnLoad:boolean = false, ignoreEsc:boolean = false) => {
  const { modals, setModal } = useContext(ModalsContext)
  const { isActive, payload } = useMemo(() => modals[id] ?? { isActive: openOnLoad, payload: null }, [modals, id, openOnLoad])

  const toggle = useCallback(() => {
    setModal({ [id]: { isActive: !isActive, payload }})
  }, [id, setModal, isActive, payload])

  const open = useCallback(() => {
    setModal({ [id]: { isActive: true, payload }})
  }, [id, setModal, payload])

	/**
	 * Close the modal keeping the payload if any
	 * @returns void
	 */
	const closeKeepingPayload = useCallback(() => {
    setModal({ [id]: { isActive: false, payload }})
  }, [id, setModal, payload])

	const closeClearingPayload = useCallback(() => {
    setModal({ [id]: { isActive: false, payload: null }})
  }, [id, setModal])

  const updatePayload = useCallback(({ payload = null, close = false, open = false }) => {
    setModal({ [id]: { isActive: open ? open : close ? false : isActive, payload }})
  }, [id, setModal, isActive])

  const handleEsc = useCallback((e:KeyboardEvent): void => {
    if (ignoreEsc) return
    if (e.key === 'Escape') closeClearingPayload()
  }, [closeClearingPayload, ignoreEsc])

  useEffect(() => {
    if(isActive) window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [isActive, handleEsc])

  return { isActive, payload, updatePayload, toggle, open, closeKeepingPayload, closeClearingPayload }
}
